import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Home as HomeIcon, Phone, MessageCircle, ArrowLeft, User, Building2, Bed, Bath, Car, Wifi, Wind, Dumbbell, ShieldCheck, TreePine, Calculator, Navigation, Heart, Mail, Send, Loader2, Waves, Users, Presentation, IndianRupee, LandPlot, X, ChevronLeft, ChevronRight, Share2, Link2, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import ReraImageBadge from '../components/ReraImageBadge';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}/${url.replace(/^\/+/, '')}`;
};

// Kerala city coordinates
const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = React.useRef(null);

  // Lightbox keyboard controls (Escape, Arrow keys)
  useEffect(() => {
    if (!lightboxOpen || !listing?.images) return;
    const len = listing.images.length;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowRight') setSelectedImage(prev => (prev + 1) % len);
      else if (e.key === 'ArrowLeft') setSelectedImage(prev => (prev - 1 + len) % len);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, listing]);
  
  // Save property state
  const [user, setUser] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  
  // Mortgage calculator state
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);

  // Email enquiry state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // WhatsApp enquiry modal state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [waForm, setWaForm] = useState({ name: '', phone: '', email: '' });
  const [waCountryCode, setWaCountryCode] = useState('+91');
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showReraPopup, setShowReraPopup] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchListing();
    checkUserAndSavedStatus();
  }, [id]);

  const checkUserAndSavedStatus = async () => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      if (userData.role === 'user') {
        setUser(userData);
        // Check if property is saved
        try {
          const response = await axios.get(`${API}/user/saved-properties`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const savedIds = response.data.map(p => p.id);
          setIsSaved(savedIds.includes(id));
        } catch (error) {
          console.error('Error checking saved status:', error);
        }
      }
    }
  };

  const handleSaveProperty = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const token = localStorage.getItem('token');
    setSavingProperty(true);
    
    try {
      if (isSaved) {
        await axios.delete(`${API}/user/save-property/${listing?.id || id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSaved(false);
      } else {
        await axios.post(`${API}/user/save-property`, 
          { listing_id: listing?.id || id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error saving property:', error);
    } finally {
      setSavingProperty(false);
    }
  };

  const shareUrl = listing ? `${window.location.origin}/property/${listing.slug || id}` : `${window.location.origin}/property/${id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
    setShowShareMenu(false);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`Check out this property on PropTick:\n\n*${listing?.title}*\n${listing?.locality}, ${listing?.city}\n\n${shareUrl}`);
    const a = document.createElement('a');
    a.href = `https://wa.me/?text=${text}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
    setShowShareMenu(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: listing?.title, text: `${listing?.title} - ${listing?.locality}, ${listing?.city}`, url: shareUrl });
      } catch { /* user cancelled share */ }
    }
    setShowShareMenu(false);
  };

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
      // Re-check saved status with actual UUID (in case URL used slug)
      if (response.data?.id && response.data.id !== id) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const savedRes = await axios.get(`${API}/user/saved-properties`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const savedIds = savedRes.data.map(p => p.id);
            setIsSaved(savedIds.includes(response.data.id));
          } catch (e) {}
        }
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const COUNTRY_CODES = [
    { code: '+91', label: 'IN +91', digits: 10, startsWith: /^[6-9]/ },
    { code: '+971', label: 'AE +971', digits: 9, startsWith: /^[2-9]/ },
    { code: '+1', label: 'US +1', digits: 10, startsWith: /^[2-9]/ },
    { code: '+1', label: 'CA +1', digits: 10, startsWith: /^[2-9]/ },
    { code: '+44', label: 'UK +44', digits: 10, startsWith: /^[1-9]/ },
    { code: '+966', label: 'SA +966', digits: 9, startsWith: /^[1-9]/ },
    { code: '+968', label: 'OM +968', digits: 8, startsWith: /^[2-9]/ },
    { code: '+974', label: 'QA +974', digits: 8, startsWith: /^[2-9]/ },
    { code: '+973', label: 'BH +973', digits: 8, startsWith: /^[1-9]/ },
    { code: '+965', label: 'KW +965', digits: 8, startsWith: /^[1-9]/ },
    { code: '+61', label: 'AU +61', digits: 9, startsWith: /^[2-9]/ },
    { code: '+65', label: 'SG +65', digits: 8, startsWith: /^[3-9]/ },
    { code: '+60', label: 'MY +60', digits: 10, startsWith: /^[1-9]/ },
    { code: '+62', label: 'ID +62', digits: 11, startsWith: /^[1-9]/ },
    { code: '+234', label: 'NG +234', digits: 10, startsWith: /^[7-9]/ },
    { code: '+66', label: 'TH +66', digits: 9, startsWith: /^[1-9]/ },
    { code: '+49', label: 'DE +49', digits: 11, startsWith: /^[1-9]/ },
    { code: '+64', label: 'NZ +64', digits: 9, startsWith: /^[2-9]/ },
    { code: '+33', label: 'FR +33', digits: 9, startsWith: /^[1-9]/ },
    { code: '+39', label: 'IT +39', digits: 10, startsWith: /^[1-9]/ },
    { code: '+34', label: 'ES +34', digits: 9, startsWith: /^[1-9]/ },
    { code: '+31', label: 'NL +31', digits: 9, startsWith: /^[1-9]/ },
    { code: '+86', label: 'CN +86', digits: 11, startsWith: /^1/ },
    { code: '+81', label: 'JP +81', digits: 10, startsWith: /^[1-9]/ },
    { code: '+7', label: 'RU +7', digits: 10, startsWith: /^[1-9]/ },
  ];

  const FAKE_NUMBERS = ['0000000000','1111111111','2222222222','3333333333','4444444444','5555555555','6666666666','7777777777','8888888888','9999999999','1234567890','0987654321','9876543210','1234512345'];

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return null;
    const country = COUNTRY_CODES.find(c => c.code === waCountryCode) || COUNTRY_CODES[0];
    if (digits.length !== country.digits) return `Enter a valid ${country.digits}-digit number`;
    if (!country.startsWith.test(digits)) return `Invalid number for ${country.label.split(' ')[0]}`;
    if (FAKE_NUMBERS.includes(digits)) return 'Please enter a real phone number';
    if (/^(\d)\1{7,}$/.test(digits)) return 'Please enter a real phone number';
    return null;
  };

  const handleContact = (type) => {
    const leadData = { listing_id: listing?.id || id, contact_type: type };
    if (user) {
      leadData.user_name = user.name || '';
      leadData.user_phone = user.phone || '';
      leadData.user_email = user.email || '';
    }
    axios.post(`${API}/leads`, leadData).catch(() => {});

    if (type === 'whatsapp') {
      trackEvent('whatsapp_click', listing?.id || id);
      // Auto-fill from logged-in user
      if (user) {
        let userPhone = user.phone || '';
        let detectedCode = '+91';
        // Detect country code from stored phone
        for (const c of COUNTRY_CODES) {
          const prefix = c.code.replace('+', '');
          if (userPhone.startsWith(prefix)) {
            detectedCode = c.code;
            userPhone = userPhone.slice(prefix.length);
            break;
          }
        }
        setWaCountryCode(detectedCode);
        setWaForm({
          name: user.name || '',
          phone: userPhone,
          email: user.email || '',
        });
      }
      setShowWhatsAppModal(true);
    } else if (type === 'call') {
      trackEvent('call_click', listing?.id || id);
      window.location.href = `tel:${listing.seller_phone}`;
    }
  };

  const handleWhatsAppSubmit = async (e) => {
    e.preventDefault();
    const name = waForm.name.trim();
    const phone = waForm.phone.trim();
    const email = waForm.email.trim();
    if (!name || !phone || !email) {
      alert('Please enter your name, phone number and email to continue.');
      return;
    }
    const phoneError = validatePhone(phone);
    if (phoneError) {
      alert(phoneError);
      return;
    }
    setWaSubmitting(true);
    // Build the WhatsApp URL BEFORE the async call so we can redirect after
    const listingUrl = `${window.location.origin}/property/${listing.slug || id}`;
    const message = encodeURIComponent(`Hi, I'm interested in your property listed on PropTick:\n\n*${listing.title}*\n${listingUrl}\n\nPlease share more details.`);
    const cleanPhone = (listing.seller_phone || '').replace(/\D/g, '').replace(/^00/, '');
    const waUrl = `https://wa.me/${cleanPhone}?text=${message}`;
    try {
      await axios.post(`${API}/enquiries`, {
        listing_id: listing.id,
        name,
        phone: waCountryCode + phone.replace(/\D/g, ''),
        email,
      });
    } catch (err) {
      console.error('Enquiry save failed:', err);
    }
    setWaSubmitting(false);
    setShowWhatsAppModal(false);
    setWaForm({ name: '', phone: '', email: '' });
    // Use location.href — Safari blocks window.open after async calls
    window.location.href = waUrl;
  };

  const handleEmailEnquiry = async (e) => {
    e.preventDefault();
    const name = emailForm.name.trim();
    const email = emailForm.email.trim();
    const phone = emailForm.phone.trim();
    if (!name || !phone || !email) {
      alert('Please enter your name, phone number and email to continue.');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/enquiry/email`, {
        listing_id: listing?.id || id,
        ...emailForm,
        name,
        email,
        phone,
      });
      setEmailSent(true);
      setTimeout(() => {
        setShowEmailForm(false);
        setEmailSent(false);
        setEmailForm({ name: '', email: '', phone: '', message: '' });
      }, 3000);
    } catch (error) {
      alert('Failed to send enquiry. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getMapQuery = (listing) => {
    if (listing.location_coords) return listing.location_coords;
    if (listing.location_link) {
      const coordMatch = listing.location_link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (coordMatch) return `${coordMatch[1]},${coordMatch[2]}`;
      const qMatch = listing.location_link.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (qMatch) return `${qMatch[1]},${qMatch[2]}`;
    }
    return `${listing.locality}, ${listing.city}, Kerala, India`;
  };

  // Calculate EMI
  const calculateEMI = () => {
    if (!listing) return { emi: 0, loanAmount: 0, downPayment: 0 };
    
    const downPayment = (listing.price * downPaymentPercent) / 100;
    const loanAmount = listing.price - downPayment;
    const monthlyRate = interestRate / 12 / 100;
    const numPayments = loanTenure * 12;
    
    if (monthlyRate === 0) {
      return { emi: loanAmount / numPayments, loanAmount, downPayment };
    }
    
    const emi = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    return { emi: Math.round(emi), loanAmount, downPayment };
  };

  const { emi, loanAmount, downPayment } = calculateEMI();

  // Amenity icons mapping
  const amenityIcons = {
    'parking': { icon: Car, label: 'Parking' },
    'security': { icon: ShieldCheck, label: '24/7 Security' },
    'gym': { icon: Dumbbell, label: 'Gym' },
    'ac': { icon: Wind, label: 'AC' },
    'garden': { icon: TreePine, label: 'Garden' },
    'waterfront': { icon: Navigation, label: 'Waterfront' },
    'sauna': { icon: Wind, label: 'Sauna' },
    'steam': { icon: Wind, label: 'Steam Room' },
    'pool': { icon: Waves, label: 'Swimming Pool' },
    'intercom': { icon: Phone, label: 'Intercom' },
    'clubhouse': { icon: Building2, label: 'Club House' },
    'meeting_room': { icon: Presentation, label: 'Meeting Room' },
    'furnished': { icon: HomeIcon, label: 'Furnished' },
    'semi_furnished': { icon: HomeIcon, label: 'Semi-Furnished' },
    'equipped_kitchen': { icon: HomeIcon, label: 'Equipped Kitchen' },
    'solar_power': { icon: ShieldCheck, label: 'Solar Power' },
    'heater': { icon: Wind, label: 'Heater' },
    'fan': { icon: Wind, label: 'Fan' },
    'electrical_provisions': { icon: ShieldCheck, label: 'Electrical Provisions' },
    'vastu_compliant': { icon: Navigation, label: 'Vastu Compliant' },
    'badminton_court': { icon: ShieldCheck, label: 'Badminton Court' },
    'basketball_court': { icon: ShieldCheck, label: 'Basketball Court' },
    'yoga': { icon: Users, label: 'Yoga' },
    'ev_charging': { icon: ShieldCheck, label: 'EV Charging' },
    'childrens_play_area': { icon: ShieldCheck, label: "Children's Play Area" },
    'basement_parking': { icon: Car, label: 'Basement Parking' },
    'smart_home': { icon: HomeIcon, label: 'Smart Home' },
    'amphitheater': { icon: Presentation, label: 'Amphitheater' },
    'jogging_track': { icon: Dumbbell, label: 'Jogging Track' },
    'reflexology_path': { icon: TreePine, label: 'Reflexology Path' },
    'drivers_room': { icon: Car, label: 'Drivers Room' },
    'terrace': { icon: HomeIcon, label: 'Terrace' },
    'kids_pool': { icon: Waves, label: 'Kids Pool' },
    'tennis_court': { icon: ShieldCheck, label: 'Tennis Court' },
    'pickleball_court': { icon: ShieldCheck, label: 'Pickleball Court' },
    'wall_climbing': { icon: Dumbbell, label: 'Wall Climbing' },
    'jacuzzi': { icon: Waves, label: 'Jacuzzi' },
    'paddle_court': { icon: ShieldCheck, label: 'Paddle Court' },
  };

  // Get amenities from listing or generate defaults
  const getAmenities = () => {
    if (!listing) return [];
    
    // If listing has amenities array, use it
    if (listing.amenities && listing.amenities.length > 0) {
      return listing.amenities.map(amenityId => {
        const amenityInfo = amenityIcons[amenityId];
        return amenityInfo || { icon: ShieldCheck, label: amenityId };
      });
    }
    
    // Fallback: Generate default amenities based on property type
    const baseAmenities = [];
    
    if (listing.property_type === 'apartment' || listing.property_type === 'villa') {
      baseAmenities.push(
        { icon: Car, label: 'Parking' },
        { icon: ShieldCheck, label: '24/7 Security' }
      );
      if (listing.price > 5000000) {
        baseAmenities.push({ icon: Dumbbell, label: 'Gym' });
      }
      if (listing.price > 10000000) {
        baseAmenities.push({ icon: Wind, label: 'AC' });
      }
    }
    if (listing.property_type === 'villa') {
      baseAmenities.push({ icon: TreePine, label: 'Garden' });
    }
    if (listing.listing_type === 'rent') {
      baseAmenities.push({ icon: Wifi, label: 'WiFi Ready' });
    }
    
    return baseAmenities;
  };

  const amenities = getAmenities();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="skeleton h-6 w-40 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="skeleton h-[450px] w-full rounded-2xl mb-4"></div>
              <div className="flex gap-2 mb-8">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 w-24 rounded-lg"></div>)}
              </div>
              <div className="skeleton h-8 w-3/4 mb-3"></div>
              <div className="skeleton h-5 w-1/2 mb-6"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl"></div>)}
              </div>
              <div className="skeleton h-4 w-full mb-2"></div>
              <div className="skeleton h-4 w-5/6 mb-2"></div>
              <div className="skeleton h-4 w-2/3"></div>
            </div>
            <div>
              <div className="skeleton h-64 w-full rounded-2xl mb-6"></div>
              <div className="skeleton h-48 w-full rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-lg text-slate-600">Property not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      <SEO
        title={`${listing.title} - ${listing.locality}, ${listing.city}`}
        description={`${listing.title} in ${listing.locality}, ${listing.city}. ${listing.bedrooms ? listing.bedrooms + ' BHK, ' : ''}${listing.size_sqft ? listing.size_sqft + ' sq.ft, ' : ''}${formatPrice(listing.price)}${listing.price_onwards ? ' onwards' : ''}. Verified property on PropTick.`}
        path={`/property/${listing?.slug || id}`}
        image={listing.images?.[0]?.startsWith('http') ? listing.images[0] : undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          "name": listing.title,
          "description": listing.description,
          "url": `https://proptick.in/property/${listing?.slug || id}`,
          "image": listing.images?.[0]?.startsWith('http') ? listing.images[0] : undefined,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": listing.locality,
            "addressRegion": listing.city,
            "addressCountry": "IN"
          },
          "offers": {
            "@type": "Offer",
            "price": listing.price,
            "priceCurrency": "INR"
          }
        }}
      />
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 py-12 animate-fade-in-up">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/properties')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to listings
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Main Image */}
              <div className="bg-white rounded-2xl overflow-hidden border border-slate-200">
                <div className="relative h-[300px] md:h-[500px] bg-slate-200"
                  onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
                  onTouchEnd={e => {
                    if (touchStartX.current === null || !listing.images || listing.images.length <= 1) return;
                    const diff = touchStartX.current - e.changedTouches[0].clientX;
                    if (Math.abs(diff) > 50) {
                      setSelectedImage(prev => diff > 0 ? (prev + 1) % listing.images.length : (prev - 1 + listing.images.length) % listing.images.length);
                    }
                    touchStartX.current = null;
                  }}
                >
                  {listing.images && listing.images.length > 0 ? (
                    <img
                      src={resolveImg(listing.images[selectedImage])}
                      alt={listing.title}
                      className="w-full h-full object-cover cursor-pointer"
                      loading="lazy"
                      decoding="async"
                      onClick={() => setLightboxOpen(true)}
                      data-testid="main-property-image"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <HomeIcon className="w-24 h-24 text-slate-400" />
                    </div>
                  )}

                  {/* Image counter on mobile */}
                  {listing.images && listing.images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 md:hidden bg-black/60 text-white text-xs font-medium px-3 py-1 rounded-full">
                      {selectedImage + 1} / {listing.images.length}
                    </div>
                  )}
                  
                  {/* Save Heart Button */}
                  <button
                    onClick={handleSaveProperty}
                    disabled={savingProperty}
                    className={`absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-all z-10 ${
                      isSaved
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'bg-white/90 text-slate-600 hover:bg-white hover:text-red-500 shadow-lg'
                    } ${savingProperty ? 'opacity-50 cursor-wait' : ''}`}
                    data-testid="save-property-btn"
                  >
                    <Heart className={`w-6 h-6 ${isSaved ? 'fill-current' : ''}`} />
                  </button>

                  {/* Share Button */}
                  <div className="absolute top-6 right-20 z-10">
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="w-12 h-12 rounded-full bg-white/90 text-slate-600 hover:bg-white hover:text-blue-600 shadow-lg flex items-center justify-center transition-all"
                      data-testid="share-button"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    {showShareMenu && (
                      <div className="absolute right-0 top-14 bg-white rounded-xl shadow-xl border border-slate-200 py-2 w-48 z-20" data-testid="share-menu">
                        <button onClick={handleCopyLink} className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors" data-testid="share-copy-link">
                          {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4 text-slate-400" />}
                          {linkCopied ? 'Link Copied!' : 'Copy Link'}
                        </button>
                        <button onClick={handleShareWhatsApp} className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors" data-testid="share-whatsapp">
                          <MessageCircle className="w-4 h-4 text-green-600" />
                          Share via WhatsApp
                        </button>
                        {navigator.share && (
                          <button onClick={handleNativeShare} className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors" data-testid="share-native">
                            <Share2 className="w-4 h-4 text-blue-600" />
                            More Options...
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {listing.rera_number && (
                    <ReraImageBadge
                      number={listing.rera_number}
                      onClick={() => setShowReraPopup(true)}
                      className="absolute right-3 top-20 z-10 w-[205px] max-w-[calc(100%_-_1.5rem)] md:right-6 md:top-24"
                      testId="rera-image-badge"
                    />
                  )}
                  
                  {listing.verified && (
                    <div className="absolute bottom-6 right-6 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-full" data-testid="verified-badge">
                      Verified Property
                    </div>
                  )}
                  {listing.listing_type === 'rent' && (
                    <div className="absolute top-6 left-6 bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-full">
                      For Rent
                    </div>
                  )}
                  {listing.property_status === 'off_plan' && (
                    <div className={`absolute ${listing.listing_type === 'rent' ? 'top-16' : 'top-6'} left-6 bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-full`} data-testid="off-plan-badge">
                      Off Plan
                    </div>
                  )}
                </div>

                {/* Thumbnail Gallery */}
                {listing.images && listing.images.length > 1 && (
                  <div className="p-4 flex gap-3 overflow-x-auto" data-testid="image-gallery">
                    {listing.images.map((img, index) => (
                      <div
                        key={index}
                        className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          selectedImage === index ? 'border-blue-600' : 'border-transparent hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedImage(index)}
                        data-testid={`thumbnail-${index}`}
                      >
                        <img src={resolveImg(img)} alt={`${listing.title} - ${index + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Property Details */}
              <div className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-6">
                  <div className="min-w-0">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" data-testid="property-title">{listing.title}</h1>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-5 h-5 flex-shrink-0" />
                      <span className="text-base sm:text-lg">{listing.locality}, {listing.city}</span>
                    </div>
                  </div>
                  <div className="sm:text-right flex-shrink-0">
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 whitespace-nowrap" data-testid="property-price">
                      {formatPrice(listing.price)}
                      {listing.listing_type === 'rent' && <span className="text-sm sm:text-base font-normal text-slate-500">{listing.rent_period === 'per_day' ? '/day' : '/month'}</span>}
                      {listing.price_onwards && <span className="text-sm sm:text-base font-normal text-slate-400 ml-1">onwards</span>}
                    </p>
                    {listing.show_price_per_sqft && listing.size_sqft && listing.price && (
                      <p className="text-sm text-slate-500 mt-1" data-testid="price-per-sqft">
                        {formatPrice(Math.round(listing.price / listing.size_sqft))} / sq ft
                      </p>
                    )}
                    {listing.price_per_cent && (
                      <p className="text-sm text-slate-500 mt-0.5" data-testid="price-per-cent">
                        {formatPrice(listing.price_per_cent)} / cent
                      </p>
                    )}
                  </div>
                </div>

                {/* Beds, Baths, Size - Key Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y border-slate-200">
                  {listing.bedrooms && (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <Bed className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold">{listing.bedrooms}{listing.bedrooms_onwards ? '+' : ''}</p>
                        <p className="text-sm text-slate-500">Beds{listing.bedrooms_onwards ? ' Onwards' : ''}</p>
                      </div>
                    </div>
                  )}
                  {listing.bathrooms && (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <Bath className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold">{listing.bathrooms}</p>
                        <p className="text-sm text-slate-500">Baths</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <HomeIcon className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-2xl font-bold">{listing.size_sqft ? listing.size_sqft.toLocaleString('en-IN') : '-'}{listing.size_sqft_onwards ? '+' : ''}</p>
                      <p className="text-sm text-slate-500">Sq.Ft{listing.size_sqft_onwards ? ' Onwards' : ''}</p>
                    </div>
                  </div>
                  {listing.show_price_per_sqft && listing.size_sqft && listing.price && listing.listing_type !== 'rent' && (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <IndianRupee className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold">{Math.round(listing.price / listing.size_sqft).toLocaleString('en-IN')}</p>
                        <p className="text-sm text-slate-500">Per Sq.Ft</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Building2 className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-lg font-bold capitalize">{listing.property_type}</p>
                      <p className="text-sm text-slate-500">Type</p>
                    </div>
                  </div>
                  {listing.land_size && (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <LandPlot className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-lg font-bold">{listing.land_size}</p>
                        <p className="text-sm text-slate-500">Land Size</p>
                      </div>
                    </div>
                  )}
                  {listing.rera_number && (
                    <button
                      onClick={() => setShowReraPopup(true)}
                      className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors text-left border border-purple-200 cursor-pointer"
                      data-testid="rera-info-card"
                    >
                      <ShieldCheck className="w-6 h-6 text-purple-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-purple-700">RERA</p>
                        <p className="text-xs text-purple-500 truncate">{listing.rera_number}</p>
                      </div>
                    </button>
                  )}
                </div>

                {/* Unit Types for Off Plan */}
                {listing.unit_types && listing.unit_types.length > 0 && (
                  <div className="mt-6" data-testid="unit-types-display">
                    <h3 className="text-xl font-semibold mb-4">Layouts</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {listing.unit_types.map((unit, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4" data-testid={`unit-display-${idx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-lg font-bold text-slate-900">{unit.bedrooms} BHK</span>
                            {unit.price && (
                              <span className="text-sm font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                                from {formatPrice(unit.price)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            {(unit.sqft_min || unit.sqft_max) && (
                              <span className="flex items-center gap-1">
                                <Bed className="w-4 h-4 text-slate-400" />
                                {unit.sqft_min && unit.sqft_max && unit.sqft_min === unit.sqft_max
                                  ? `${unit.sqft_min} sqft`
                                  : unit.sqft_min && unit.sqft_max
                                    ? `${unit.sqft_min} - ${unit.sqft_max} sqft`
                                    : unit.sqft_min
                                      ? `${unit.sqft_min} sqft`
                                      : `${unit.sqft_max} sqft`}
                              </span>
                            )}
                            {unit.bathrooms && (
                              <span className="flex items-center gap-1">
                                <Bath className="w-4 h-4 text-slate-400" />
                                {unit.bathrooms} Bath{unit.bathrooms > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {amenities.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-4">Amenities</h3>
                    <div className="flex flex-wrap gap-3">
                      {amenities.map((amenity, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full"
                        >
                          <amenity.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{amenity.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Connectivity */}
                {listing.connectivity && Object.values(listing.connectivity).some(v => v) && (
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-4">Connectivity</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'railway_station', label: 'Railway Station', icon: '🚉' },
                        { key: 'hospital', label: 'Hospital', icon: '🏥' },
                        { key: 'police_station', label: 'Police Station', icon: '🏛' },
                        { key: 'school', label: 'School', icon: '🎓' },
                        { key: 'mall', label: 'Mall', icon: '🛍' },
                        { key: 'airport', label: 'Airport', icon: '✈' }
                      ].filter(({ key }) => listing.connectivity[key])
                       .map(({ key, label, icon }) => (
                        <div key={key} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`connectivity-${key}`}>
                          <span className="text-lg">{icon}</span>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{label}</p>
                            <p className="text-xs text-slate-500">{listing.connectivity[key]}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-4">Description</h3>
                  <p className="text-base leading-relaxed text-slate-600 whitespace-pre-line" data-testid="property-description">{listing.description}</p>
                </div>
              </div>

              {/* Location Map */}
              <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="location-map-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Navigation className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Location</h3>
                    <p className="text-sm text-slate-500">{listing.locality}, {listing.city}</p>
                  </div>
                </div>

                <a
                  href={listing.location_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.locality + ', ' + listing.city + ', Kerala, India')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                  style={{ height: '300px' }}
                  data-testid="location-map-link"
                >
                  <iframe
                    title="Property Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(getMapQuery(listing))}&output=embed&z=15`}
                  />
                </a>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{listing.locality}, {listing.city}, Kerala</span>
                  </div>
                  <a
                    href={listing.location_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.locality + ', ' + listing.city + ', Kerala, India')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <span>Get Directions</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Listed By & Mortgage Calculator */}
            <div className="lg:col-span-1 space-y-6">
              {/* Listed By Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-8">
                <h3 className="text-xl font-semibold mb-6">Listed by</h3>

                <div className="bg-slate-50 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    {listing.seller_profile_pic ? (
                      <img src={resolveImg(listing.seller_profile_pic)} alt={listing.seller_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : listing.seller_type === 'owner' ? (
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-lg" data-testid="seller-name">{listing.seller_name}</p>
                      <p className="text-sm text-slate-500 capitalize">{listing.seller_type}</p>
                    </div>
                  </div>
                  {listing.rera_number && (
                    <p className="text-xs text-slate-400 mt-2" data-testid="rera-number">RERA: {listing.rera_number}</p>
                  )}
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleContact('whatsapp')}
                    className="w-full h-14 rounded-full bg-green-600 text-white font-medium hover:bg-green-700 transition-all shadow-lg shadow-green-500/20 flex items-center justify-center gap-3"
                    data-testid="whatsapp-button"
                  >
                    <MessageCircle className="w-6 h-6" />
                    Contact via WhatsApp
                  </button>
                  {listing.seller_email && (
                  <button
                    onClick={() => setShowEmailForm(!showEmailForm)}
                    className="w-full h-14 rounded-full bg-violet-600 text-white font-medium hover:bg-violet-700 transition-all shadow-lg shadow-violet-500/20 flex items-center justify-center gap-3"
                    data-testid="email-enquiry-button"
                  >
                    <Mail className="w-6 h-6" />
                    Send Email Enquiry
                  </button>
                  )}
                </div>

                {showEmailForm && (
                  <div className="mt-4 bg-slate-50 rounded-2xl p-5 border border-slate-200" data-testid="email-enquiry-form">
                    {emailSent ? (
                      <div className="text-center py-6">
                        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Send className="w-7 h-7 text-green-600" />
                        </div>
                        <p className="text-green-700 font-semibold text-lg">Enquiry Sent!</p>
                        <p className="text-slate-500 text-sm mt-1">The seller will get back to you soon.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleEmailEnquiry} className="space-y-3">
                        <h4 className="font-semibold text-slate-800 mb-2">Send Enquiry</h4>
                        <input
                          type="text"
                          placeholder="Your Name"
                          required
                          value={emailForm.name}
                          onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                          className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          data-testid="enquiry-name"
                        />
                        <input
                          type="email"
                          placeholder="Your Email"
                          required
                          value={emailForm.email}
                          onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                          className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          data-testid="enquiry-email"
                        />
                        <input
                          type="tel"
                          placeholder="Your Phone"
                          required
                          value={emailForm.phone}
                          onChange={(e) => setEmailForm({ ...emailForm, phone: e.target.value })}
                          className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          data-testid="enquiry-phone"
                        />
                        <textarea
                          placeholder="I'm interested in this property..."
                          required
                          rows={3}
                          value={emailForm.message}
                          onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                          data-testid="enquiry-message"
                        />
                        <button
                          type="submit"
                          disabled={sendingEmail}
                          className="w-full h-12 rounded-full bg-violet-600 text-white font-medium hover:bg-violet-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="send-enquiry-submit"
                        >
                          {sendingEmail ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5" />
                              Send Enquiry
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                )}

                <p className="text-xs text-slate-500 mt-6 text-center">
                  By contacting, you agree to share your contact information with the seller
                </p>
              </div>

              {/* Mortgage Calculator - Only for Buy properties */}
              {listing.listing_type !== 'rent' && (
                <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="mortgage-calculator">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <Calculator className="w-5 h-5 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold">Mortgage Calculator</h3>
                  </div>

                  {/* EMI Display */}
                  <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-6 mb-6 text-white">
                    <p className="text-sm opacity-90 mb-1">Estimated Monthly EMI</p>
                    <p className="text-4xl font-bold" data-testid="emi-amount">₹{emi.toLocaleString('en-IN')}</p>
                  </div>

                  {/* Down Payment Options */}
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-slate-700 mb-3">Down Payment</label>
                    <div className="flex gap-2">
                      {[15, 20, 30].map((percent) => (
                        <button
                          key={percent}
                          onClick={() => setDownPaymentPercent(percent)}
                          className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                            downPaymentPercent === percent
                              ? 'bg-violet-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                          data-testid={`down-payment-${percent}`}
                        >
                          {percent}%
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      ₹{downPayment.toLocaleString('en-IN')} down payment
                    </p>
                  </div>

                  {/* Interest Rate */}
                  <div className="mb-5">
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-slate-700">Interest Rate</label>
                      <span className="text-sm font-semibold text-violet-600">{interestRate}%</span>
                    </div>
                    <input
                      type="range"
                      min="6"
                      max="15"
                      step="0.1"
                      value={interestRate}
                      onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                      data-testid="interest-rate-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>6%</span>
                      <span>15%</span>
                    </div>
                  </div>

                  {/* Loan Tenure */}
                  <div className="mb-5">
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-slate-700">Loan Tenure</label>
                      <span className="text-sm font-semibold text-violet-600">{loanTenure} years</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="30"
                      step="1"
                      value={loanTenure}
                      onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                      data-testid="loan-tenure-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>5 yrs</span>
                      <span>30 yrs</span>
                    </div>
                  </div>

                  {/* Loan Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Property Price</span>
                      <span className="font-medium">{formatPrice(listing.price)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Down Payment ({downPaymentPercent}%)</span>
                      <span className="font-medium">₹{downPayment.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-slate-200 pt-3">
                      <span className="text-slate-600">Loan Amount</span>
                      <span className="font-semibold text-violet-600">₹{loanAmount.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mt-4 text-center">
                    *Estimates only. Actual EMI may vary based on bank rates.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && listing.images && listing.images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchStartX.current === null) return;
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              setSelectedImage(prev => diff > 0 ? (prev + 1) % listing.images.length : (prev - 1 + listing.images.length) % listing.images.length);
            }
            touchStartX.current = null;
          }}
          data-testid="image-lightbox"
        >
          <button onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
            data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
          {listing.images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => (prev - 1 + listing.images.length) % listing.images.length); }}
                className="absolute left-3 z-50 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
                data-testid="lightbox-prev">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => (prev + 1) % listing.images.length); }}
                className="absolute right-3 z-50 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition"
                data-testid="lightbox-next">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img src={resolveImg(listing.images[selectedImage])} alt={listing.title}
            className="max-w-[95vw] max-h-[90vh] object-contain" onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-1.5 rounded-full">
            {selectedImage + 1} / {listing.images.length}
          </div>
        </div>
      )}

      {/* RERA Details Popup */}
      {showReraPopup && listing.rera_number && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={() => setShowReraPopup(false)} data-testid="rera-popup-overlay">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rera-popup">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-2xl font-bold text-slate-900">RERA Details</h2>
                <button onClick={() => setShowReraPopup(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="rera-popup-close">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {listing.rera_link && (
                <a href={listing.rera_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 mb-5">
                  <Navigation className="w-4 h-4" />
                  {listing.rera_link}
                </a>
              )}

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-5">
                <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">Registered</p>
                <p className="text-lg font-bold text-slate-900">{listing.rera_number}</p>
              </div>

              {listing.rera_images && listing.rera_images.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">RERA Documents</p>
                  <div className="grid grid-cols-2 gap-3">
                    {listing.rera_images.map((img, idx) => (
                      <div key={idx} className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                        <img src={resolveImg(img)} alt={`RERA Document ${idx + 1}`} className="w-full h-auto object-contain" loading="lazy" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Enquiry Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={() => setShowWhatsAppModal(false)} data-testid="whatsapp-modal-overlay">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="whatsapp-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Contact via WhatsApp</h3>
                  <p className="text-xs text-slate-500">Share your details to connect with the seller</p>
                </div>
              </div>
              <button onClick={() => setShowWhatsAppModal(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="whatsapp-modal-close">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleWhatsAppSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  placeholder="Your full name"
                  value={waForm.name}
                  onChange={(e) => setWaForm({ ...waForm, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all"
                  data-testid="wa-form-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <select
                    value={waCountryCode}
                    onChange={(e) => setWaCountryCode(e.target.value)}
                    className="h-11 px-2 rounded-lg border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all w-28 flex-shrink-0"
                    data-testid="wa-form-country-code"
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    required
                    placeholder={waCountryCode === '+91' ? '98765 43210' : 'Phone number'}
                    value={waForm.phone}
                    onChange={(e) => setWaForm({ ...waForm, phone: e.target.value })}
                    className={`w-full h-11 px-4 rounded-lg border text-sm focus:ring-2 transition-all ${
                      waForm.phone && validatePhone(waForm.phone)
                        ? 'border-red-300 focus:ring-red-100 focus:border-red-500'
                        : 'border-slate-200 focus:ring-green-100 focus:border-green-500'
                    }`}
                    data-testid="wa-form-phone"
                  />
                </div>
                {waForm.phone && validatePhone(waForm.phone) && (
                  <p className="text-xs text-red-500 mt-1">{validatePhone(waForm.phone)}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={waForm.email}
                  onChange={(e) => setWaForm({ ...waForm, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all"
                  data-testid="wa-form-email"
                />
              </div>
              <button
                type="submit"
                disabled={waSubmitting}
                className="w-full h-12 rounded-full bg-green-600 text-white font-medium hover:bg-green-700 transition-all shadow-lg shadow-green-500/20 flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="wa-form-submit"
              >
                {waSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5" />
                    Continue to WhatsApp
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
